import { datadogLogs } from "@datadog/browser-logs";
import { checkSocialLogin } from "@repo/shopify/api/checkSocialLogin";
import useTracking from "@repo/shopify/hooks/useTracking";
import { discoverCaaEndpoints } from "@repo/shopify/utils/caaDiscovery";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "@repo/shopify/utils/pkce";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@atoms/Button";

import Login from "@organisms/Header/HeaderAccount/Login";
import Register from "@organisms/Header/HeaderAccount/Register";
import Reset from "@organisms/Header/HeaderAccount/Reset";
import { SocialLogin } from "@organisms/Header/HeaderAccount/SocialLogin";

import { NOTIFICATIONS } from "@const/notifications";
import { SHOPIFY_DOMAIN, URLS } from "@const/urls";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const TOSS_EMAILS = ["toss.im", "tosspayments.com", "tossinsight.im"];

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

const CAA_CLIENT_ID = import.meta.env.VITE_JP_CAA_CLIENT_ID;

export default function HeaderAccount() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { lang } = useLanguage();

  const { openAccount, openModal, openToast } = useLayoutStore();
  const { login, update } = useCustomerStore();
  const { createToken, fetchCustomer, createCustomer, resetPassword, createCustomerLoading } = useCustomerQuery();
  const { gtmLogin } = useTracking();

  const [formType, setFormType] = useState<"login" | "register" | "forgot">("login");

  const isInApp = useMemo(() => {
    const userAgent = navigator.userAgent.toUpperCase();

    return (
      userAgent.includes("NAVER") ||
      userAgent.includes("KAKAO") ||
      userAgent.includes("INSTAGRAM") ||
      userAgent.includes("FBAN") ||
      userAgent.includes("FBAV")
    );
  }, []);

  useEffect(() => {
    if (isInApp) {
      openModal({
        title: t("login.in_app_browser_title"),
        content: (
          <div>
            <Trans i18nKey="login.in_app_browser_message" />
          </div>
        ),
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
      });
    }
  }, [isInApp]);

  const submit = async ({
    email,
    password,
    acceptsMarketing,
  }: {
    email: string;
    password: string;
    acceptsMarketing?: boolean;
  }) => {
    // jp는 New Customer Accounts라 classic 로그인/가입 대신 Customer Account API OAuth로 처리
    if (lang === "jp") {
      void redirectToCaaLogin();
      return;
    }

    // 소셜로그인 계정인지 비활성화 계정인지 체크
    const response = await checkSocialLogin({ email, domain: lang });

    // 비활성화된 계정일 경우
    if (response?.isDeactivated) {
      openModal({
        title: "Account Access Restricted",
        content: (
          <div>
            <Trans i18nKey="login.account_disable" />
          </div>
        ),
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
      });
      return;
    }

    // 소셜 계정 (이메일 로그인 불가)
    if (response && response.loginType !== "email") {
      if (formType === "forgot") openToast({ message: t("success.password_reset_social_email") });
      else openToast(NOTIFICATIONS.WARNING_SOCIAL_ACCOUNT({ social: response.loginType }));
      return;
    }

    switch (formType) {
      case "register": {
        const { data } = await createCustomer({
          variables: { input: { email, password, acceptsMarketing } },
        });
        if (data?.customerCreate.customerUserErrors.length) return;
        await handleLogin({ email, password });
        break;
      }
      case "forgot": {
        await resetPassword({ variables: { email } });
        break;
      }
      case "login":
      default:
        await handleLogin({ email, password });
        break;
    }
  };

  const handleLogin = async ({ email, password }: { email: string; password: string }) => {
    const { data } = await createToken({ variables: { input: { email, password } } });

    if (data?.customerAccessTokenCreate.customerUserErrors.length) {
      return;
    }

    if (!data?.customerAccessTokenCreate.customerAccessToken) return;

    const { data: customerData } = await fetchCustomer({
      variables: { customerAccessToken: data.customerAccessTokenCreate.customerAccessToken.accessToken },
    });

    login(data.customerAccessTokenCreate.customerAccessToken);

    if (customerData?.customer) update(customerData.customer);

    if (TOSS_EMAILS.includes(email.split("@")[1])) {
      if (!customerData?.customer.tags.includes("PARTNERSHIP_TOSS")) {
        openModal({
          title: "Verify Your Account",
          content: "토스 임직원 계정은 이메일 인증을 완료해야 할인 혜택이 적용됩니다. 이메일 수신함을 확인해 주세요.",
          isHideCancelButton: true,
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
            },
          },
        });
      }
    }

    const loginAfterUrl = sessionStorage.getItem("loginAfterUrl");
    if (loginAfterUrl) {
      navigate(loginAfterUrl);
    }

    sessionStorage.removeItem("loginAfterUrl");

    openAccount(false);
    openToast(NOTIFICATIONS.SUCCESS_LOGIN);
    gtmLogin(
      "email",
      customerData?.customer?.id?.replace("gid://shopify/Customer/", ""),
      customerData?.customer?.email ?? undefined,
    );
  };

  /** jp locale (New Customer Accounts) 전용 — Customer Account API OAuth 인증 페이지로 리다이렉트 */
  const redirectToCaaLogin = async () => {
    try {
      const { authorizationEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      sessionStorage.setItem("caaCodeVerifier", codeVerifier);
      sessionStorage.setItem("caaState", state);

      const params = new URLSearchParams({
        client_id: CAA_CLIENT_ID,
        redirect_uri: `${window.location.origin}${URLS.AUTH_CALLBACK}`,
        response_type: "code",
        scope: "openid email customer-account-api:full",
        state,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        locale: "ja", // jp 전용 로그인이으로 브라우저/계정 언어 설정과 무관하게 항상 일본어 로그인 화면 강제 TODO: 할말 고민
      });

      window.location.href = `${authorizationEndpoint}?${params.toString()}`;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      datadogLogs.logger.error("[CAA] redirectToCaaLogin failed", { message });
      openToast(NOTIFICATIONS.ERROR_SERVER);
    }
  };

  return (
    <div className="pc:top-0 pc:h-auto pc:max-h-dvh pc:w-1/3 pc:p-5 fixed top-13.5 right-0 z-60 flex h-dvh w-full flex-col gap-5 bg-white px-4 pb-4 lg:w-1/4 dark:bg-black dark:text-white">
      <div className="flex justify-between">
        <span className="col-span-8 uppercase">Login/Register</span>
        <Button
          as="text"
          className="pc:visible invisible"
          onClick={() => {
            openAccount(false);
          }}
        >
          Close
        </Button>
      </div>
      <div className="flex flex-col gap-10">
        {lang !== "jp" ? (
          <div className="flex flex-col gap-5">
            {formType === "login" && (
              <Login
                onClickFindPassword={() => {
                  setFormType("forgot");
                }}
                onClickRegister={() => {
                  setFormType("register");
                }}
                submit={submit}
              />
            )}
            {formType === "register" && (
              <Register
                createCustomerLoading={createCustomerLoading}
                onClickBack={() => {
                  setFormType("login");
                }}
                submit={submit}
              />
            )}
            {formType === "forgot" && (
              <Reset
                onClickBack={() => {
                  setFormType("login");
                }}
                onClickRegister={() => {
                  setFormType("register");
                }}
                submit={submit}
              />
            )}
          </div>
        ) : (
          // TODO: 디자인 필요 (Header에서 비밀번호 찾기도 막아둬야함)
          <Button
            as="square"
            onClick={() => {
              void redirectToCaaLogin();
            }}
          >
            Login with Shopify
          </Button>
        )}
        <div className="flex flex-col gap-2.5">
          <span className="text-title">Social Login</span>
          <SocialLogin />
        </div>
      </div>
    </div>
  );
}
